import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RowCard, StatusPill } from '../patterns';
import type { StatusVariant } from '../patterns';
import type { NutritionSummary } from '../../services/nutritionService';

type SoilRounds = NutritionSummary['rounds'];

const PILL: Record<string, StatusVariant> = {
  completed: 'ok',
  in_progress: 'warn',
  generated: 'info',
  not_generated: 'warn',
};

const LABEL: Record<string, string> = {
  completed: 'nutrition.dashboard.foliarRounds.status.completed',
  in_progress: 'nutrition.dashboard.foliarRounds.status.inProgress',
  generated: 'nutrition.dashboard.foliarRounds.status.generated',
  not_generated: 'nutrition.dashboard.foliarRounds.status.notGenerated',
};

export const NutritionFoliarRoundsCard: React.FC<{ programmes: SoilRounds }> = ({ programmes }) => {
  const { t } = useTranslation();
  return (
    <section className="ntr-dash-card" data-testid="nutrition-foliar-rounds-card">
      <header className="ntr-dash-card__head">
        {/*
          #443: the scope pill lives INSIDE the card header it describes. It
          used to sit in a bare, unclassed div at the top of the dashboard's
          left column — a `flex-direction: column` stack that also holds the
          sign-off card and the soil coverage card — so "Foliar only" read
          top-to-bottom as labelling the SOIL card too.
        */}
        <div className="ntr-dash-card__headgroup">
          <h3 className="ntr-dash-card__title">{t('nutrition.dashboard.foliarRounds.title')}</h3>
          <span data-testid="nutrition-scope-rounds">
            <StatusPill variant="info">{t('nutrition.dashboard.scopeFoliar')}</StatusPill>
          </span>
        </div>
        <Link to="/nutrition/foliar" className="ntr-dash-card__viewall">
          {t('nutrition.dashboard.foliarRounds.viewAll')}
        </Link>
      </header>
      <div className="ntr-dash-card__body">
        {programmes.length === 0 && (
          <p className="ntr-dash-card__empty">{t('nutrition.dashboard.foliarRounds.empty')}</p>
        )}
        {programmes.map((prog) => (
          <React.Fragment key={prog.programmeId}>
            <div className="ntr-dash-prog__season">{prog.season}</div>
            {prog.rounds.map((r) => (
              <RowCard key={r.roundId} testId={`foliar-round-${prog.programmeId}-${r.roundId}`}>
                <div className="ntr-dash-round">
                  <span className="ntr-dash-round__name">{r.name}</span>
                  <span className="ntr-dash-round__counts">{r.completed}/{r.total}</span>
                  <StatusPill variant={PILL[r.status] ?? 'info'}>
                    {t(LABEL[r.status] ?? r.status)}
                  </StatusPill>
                </div>
              </RowCard>
            ))}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

export default NutritionFoliarRoundsCard;
